import { api } from './api.js';

// 046: typed client for the /memberships API. Types mirror the backend's
// modules/memberships/serializer.ts wire shape EXACTLY (044 seam lesson:
// `id` — never `_id` — and nested user/supplier/assigned_country objects or
// null, never bare foreign-key ids).

// 367: any organisation role on the Roles page (Host, or a custom one); the Supplier role is retired.
export type MembershipRole = string;
export type EmploymentStatus = 'active' | 'inactive' | 'terminated';

export interface Membership {
  id: string;
  user: { id: string; name: string; email: string } | null;
  supplier: { id: string; company_name: string } | null;
  role: MembershipRole;
  /** 377 (#1233): a Host who may see the supplier's sales. Absent from an older backend: off. */
  can_see_sales?: boolean;
  employee_code: string | null;
  employment_status: EmploymentStatus;
  assigned_country: { id: string; name: string } | null;
  notes: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ListMembershipsParams {
  supplier_id?: string;
  role?: MembershipRole;
  employment_status?: EmploymentStatus;
  include_deleted?: boolean;
  /** 162: pagination and search. */
  page?: number;
  limit?: number;
  q?: string;
}

export interface PatchMembershipBody {
  employee_code?: string | null;
  employment_status?: EmploymentStatus;
  assigned_country_id?: string | null;
  notes?: string | null;
}

function toQuery(params: ListMembershipsParams): string {
  const qs = new URLSearchParams();
  if (params.supplier_id) qs.set('supplier_id', params.supplier_id);
  if (params.role) qs.set('role', params.role);
  if (params.employment_status) qs.set('employment_status', params.employment_status);
  if (params.include_deleted) qs.set('include_deleted', '1');
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const membershipsApi = {
  list: (params: ListMembershipsParams = {}) =>
    // 162: `pages` is present only when a page was asked for — the endpoint still answers the
    // whole set to a caller that asks for none.
    api.get<{ items: Membership[]; pages: number }>(`/memberships${toQuery(params)}`),

  get: (id: string) => api.get<Membership>(`/memberships/${id}`),

  update: (id: string, body: PatchMembershipBody) =>
    api.patch<Membership>(`/memberships/${id}`, body),

  remove: (id: string) => api.delete(`/memberships/${id}`),

  restore: (id: string) => api.post<Membership>(`/memberships/${id}/restore`),
};
